import { useRef, useState } from 'react';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { pluralWithCount } from '../../lib/plural';
import { showConfirm } from '../../lib/telegram';
import { GripIcon } from '../../ui/icons';
import { Pill } from '../../ui/parts';
import { FormSkeleton, LoadFailed } from '../org-parts';
import { useSortable } from '../use-sortable';
import { useAdminDirections, useDirectionMutations } from '../api';
import type { Direction } from '../types';

const staffWord = (n: number) => pluralWithCount(n, 'специалист', 'специалиста', 'специалистов');
const serviceWord = (n: number) => pluralWithCount(n, 'услуга', 'услуги', 'услуг');
const clientWord = (n: number) => pluralWithCount(n, 'клиент', 'клиента', 'клиентов');

/** Насколько далеко надо увести палец влево, чтобы это считалось свайпом, а не промахом. */
const SWIPE_OPEN = 64;

/**
 * Подэкран «Категории услуг». Порядок задаётся перетаскиванием: он же и есть
 * порядок в списке услуг и при записи, а «сортировать по алфавиту» — не то, что
 * нужно организации, где первичная категория должна стоять первой.
 */
export function OrgCategories() {
  const writable = useWritable();
  const sheet = useSheet();
  const toast = useToast();
  const directions = useAdminDirections();
  const { reorder, remove } = useDirectionMutations();

  const [openId, setOpenId] = useState<number | null>(null);
  const swipe = useRef<{ id: number; startX: number; startY: number; decided: boolean } | null>(null);
  /** Свайп заканчивается кликом по строке — его надо погасить, иначе поверх откроется шторка. */
  const swiped = useRef(false);

  // Хук стоит выше ранних возвратов: список может быть ещё пуст, но вызов
  // должен случаться на каждом рендере — таково правило хуков.
  const loaded = directions.data ?? [];
  const byId = new Map(loaded.map((d) => [d.id, d]));
  const sort = useSortable(
    loaded.map((d) => d.id),
    (ids) => reorder.mutate(ids, {
      onSuccess: sort.clear,
      onError: (e) => {
        sort.clear();
        toast(e instanceof ApiError ? e.message : 'Не получилось изменить порядок');
      },
    }),
    '.cat',
  );

  useScreen('Категории услуг', null);

  if (directions.isError) {
    return <Screen><LoadFailed busy={directions.isFetching} onRetry={() => void directions.refetch()} /></Screen>;
  }
  if (directions.isPending) {
    return <Screen><FormSkeleton rows={4} /></Screen>;
  }

  const list = sort.list.map((id) => byId.get(id)).filter(Boolean) as Direction[];
  const dragId = sort.dragId;

  // --- свайп влево ----------------------------------------------------------

  function startSwipe(e: React.PointerEvent, id: number) {
    // Нажатие на ручку — это перетаскивание; горизонтальное дрожание пальца
    // при нём не должно вскрывать кнопку удаления.
    if (!writable || (e.target as HTMLElement).closest('.grip')) return;
    swiped.current = false;
    swipe.current = { id, startX: e.clientX, startY: e.clientY, decided: false };
  }

  function moveSwipe(e: React.PointerEvent) {
    const s = swipe.current;
    if (!s || s.decided) return;
    const dx = e.clientX - s.startX;
    const dy = Math.abs(e.clientY - s.startY);
    // Вертикальное движение — это скролл списка, а не свайп по строке.
    if (dy > Math.abs(dx)) { swipe.current = null; return; }
    if (dx < -SWIPE_OPEN) {
      s.decided = true;
      swiped.current = true;
      setOpenId(s.id);
    } else if (dx > SWIPE_OPEN) {
      s.decided = true;
      swiped.current = true;
      setOpenId(null);
    }
  }

  // --- удаление -------------------------------------------------------------

  async function del(d: Direction) {
    if (d.services > 0) {
      toast(`В категории ${serviceWord(d.services)}. Сначала перенесите ${d.services === 1 ? 'её' : 'их'}`, { ms: 4000 });
      return;
    }
    if (d.children > 0) {
      sheet.open(<DeleteBlockedSheet direction={d} />);
      return;
    }
    if (d.staff > 0) {
      sheet.open(<MoveStaffSheet direction={d} all={loaded} />);
      return;
    }
    const ok = await showConfirm(`Удалить категорию «${d.title}»?`, { title: 'Удаление', destructive: true });
    if (!ok) return;
    remove.mutate({ id: d.id }, {
      onSuccess: () => {
        setOpenId(null);
        toast('Категория удалена');
      },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось удалить'),
    });
  }

  return (
    <Screen>
      <p className="lead">
        Категория объединяет специалистов, услуги и закреплённых клиентов. Перетащите
        категории, чтобы изменить порядок — в этом же порядке их увидит клиент при записи.
      </p>

      {/* Кнопка над списком: у организации с десятком категорий добираться до неё
          прокруткой через весь список пришлось бы каждый раз. Так же стоит
          «Добавить услугу» на экране услуг и «Добавить» в списке организаций. */}
      {writable ? (
        <button className="add" onClick={() => sheet.open(<AddCategorySheet />)}>＋ Добавить категорию</button>
      ) : null}

      <div className="cats">
        {list.map((d) => (
          <div
            className={`cat${dragId === d.id ? ' is-drag' : ''}${openId === d.id ? ' is-open' : ''}`}
            key={d.id}
          >
            <div
              className="cat-face"
              onPointerDown={(e) => startSwipe(e, d.id)}
              onPointerMove={moveSwipe}
              onPointerUp={() => { swipe.current = null; }}
              onPointerCancel={() => { swipe.current = null; }}
            >
              {writable ? (
                <span
                  className="grip"
                  role="button"
                  tabIndex={-1}
                  aria-label={`Переставить «${d.title}»`}
                  {...sort.gripProps(d.id)}
                >
                  <GripIcon />
                </span>
              ) : null}

              <button
                className="cat-body"
                type="button"
                disabled={!writable}
                onClick={() => {
                  if (swiped.current) { swiped.current = false; return; }
                  if (openId === d.id) setOpenId(null);
                  else sheet.open(<CategorySheet direction={d} />);
                }}
              >
                <b>{d.title}</b>
                <span>{summary(d)}</span>
              </button>

              {d.is_active ? null : <Pill kind="done">скрыта</Pill>}
            </div>

            {writable ? (
              <button
                className="cat-del"
                type="button"
                tabIndex={openId === d.id ? 0 : -1}
                aria-hidden={openId !== d.id}
                onClick={() => void del(d)}
              >
                Удалить
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {list.length === 0 ? <p className="lead">Категорий пока нет.</p> : null}
    </Screen>
  );
}


/**
 * Строка состава. Показываем только то, что есть: ноль сущностей числом не
 * выводим — пустая категория должна подсказывать следующий шаг, а не считать нули.
 */
function summary(d: Direction): string {
  if (!d.is_active) {
    if (d.children > 0) return `${clientWord(d.children)} занимаются · новых закрепить нельзя`;
    return 'Не видно при записи';
  }
  if (!d.staff && !d.services) return 'Пока пусто — добавьте специалистов и услуги';
  if (d.staff && !d.services) return `${staffWord(d.staff)} · услуг пока нет`;
  if (!d.staff && d.services) return `${serviceWord(d.services)} · специалистов пока нет`;
  const parts = [staffWord(d.staff), serviceWord(d.services)];
  if (d.children > 0) parts.push(clientWord(d.children));
  return parts.join(' · ');
}

/** Что можно сделать с категорией. Удаление живёт в свайпе — здесь его нет намеренно. */
function CategorySheet({ direction }: { direction: Direction }) {
  const sheet = useSheet();
  const toast = useToast();
  const { update } = useDirectionMutations();

  const setActive = (isActive: boolean) => update.mutate({ id: direction.id, isActive }, {
    onSuccess: () => {
      sheet.close();
      toast(isActive ? 'Категория снова активна' : 'Категория скрыта.', {
        action: isActive ? undefined : { label: 'Вернуть', onClick: () => update.mutate({ id: direction.id, isActive: true }) },
      });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
  });

  return (
    <>
      <h3>{direction.title}</h3>
      <p>{summary(direction)}</p>

      <div className="rows">
        <button className="rw" onClick={() => sheet.open(<RenameCategorySheet direction={direction} />)}>
          <span className="rw-body"><b>Переименовать</b></span>
        </button>
        <button className="rw" disabled={update.isPending} onClick={() => setActive(!direction.is_active)}>
          <span className="rw-body">
            <b>{direction.is_active ? 'Скрыть' : 'Показать'}</b>
            <span>{direction.is_active ? 'Не будет видно при записи' : 'Снова доступна для новых'}</span>
          </span>
        </button>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
      </div>
    </>
  );
}

function RenameCategorySheet({ direction }: { direction: Direction }) {
  const sheet = useSheet();
  const toast = useToast();
  const { update } = useDirectionMutations();
  const [title, setTitle] = useState(direction.title);

  return (
    <>
      <h3>Переименовать категорию</h3>
      <div className="field">
        <label htmlFor="dTitle">Название</label>
        <input id="dTitle" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={update.isPending || title.trim().length < 2 || title.trim() === direction.title}
          onClick={() =>
            update.mutate({ id: direction.id, title: title.trim() }, {
              onSuccess: () => {
                toast('Сохранено');
                sheet.close();
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
            })
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

/** Закреплённые клиенты — стоп для удаления: их записи важнее уборки в справочнике. */
function DeleteBlockedSheet({ direction }: { direction: Direction }) {
  const sheet = useSheet();
  const toast = useToast();
  const { update } = useDirectionMutations();

  return (
    <>
      <h3>Удалить нельзя</h3>
      <p>
        К категории закреплены {clientWord(direction.children)}. Открепите их или скройте
        категорию — скрытая не видна при записи, но текущие записи продолжаются.
      </p>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Понятно</button>
        <button
          className="btn solid"
          disabled={update.isPending || !direction.is_active}
          onClick={() =>
            update.mutate({ id: direction.id, isActive: false }, {
              onSuccess: () => {
                toast('Категория скрыта.', {
                  action: { label: 'Вернуть', onClick: () => update.mutate({ id: direction.id, isActive: true }) },
                });
                sheet.close();
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
            })
          }
        >
          Скрыть вместо удаления
        </button>
      </div>
    </>
  );
}

/**
 * Специалисты без категории в схеме не живут, а молча перекидывать людей нельзя —
 * поэтому у удаления есть один вопрос: куда их перевести.
 */
function MoveStaffSheet({ direction, all }: { direction: Direction; all: Direction[] }) {
  const sheet = useSheet();
  const toast = useToast();
  const { remove } = useDirectionMutations();
  const targets = all.filter((d) => d.id !== direction.id);
  const [moveTo, setMoveTo] = useState<number | null>(targets[0]?.id ?? null);

  return (
    <>
      <h3>Удалить «{direction.title}»?</h3>
      <p>
        {staffWord(direction.staff)} {direction.staff === 1 ? 'останется' : 'останутся'} в организации —
        выберите, в какую категорию {direction.staff === 1 ? 'его' : 'их'} перевести.
      </p>
      <p className="lead">Отменить будет нельзя.</p>

      <div className="field">
        <label htmlFor="moveTo">Перевести специалистов в</label>
        {targets.length ? (
          <select id="moveTo" value={moveTo ?? ''} onChange={(e) => setMoveTo(Number(e.target.value))}>
            {targets.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        ) : (
          <span className="hint">Других категорий нет — сначала создайте ту, куда их перевести.</span>
        )}
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn ghost-danger"
          disabled={remove.isPending || moveTo === null}
          onClick={() =>
            remove.mutate({ id: direction.id, moveStaffTo: moveTo ?? undefined }, {
              onSuccess: () => {
                toast('Категория удалена');
                sheet.close();
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось удалить'),
            })
          }
        >
          {remove.isPending ? 'Удаляем…' : 'Удалить'}
        </button>
      </div>
    </>
  );
}

function AddCategorySheet() {
  const sheet = useSheet();
  const toast = useToast();
  const { create } = useDirectionMutations();
  const [title, setTitle] = useState('');

  return (
    <>
      <h3>Новая категория</h3>
      <div className="field">
        <label htmlFor="ndTitle">Название</label>
        <input
          id="ndTitle"
          type="text"
          placeholder="например, «Нейропсихолог»"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={create.isPending || title.trim().length < 2}
          onClick={() =>
            create.mutate({ title: title.trim() }, {
              onSuccess: () => {
                toast('Категория создана. Добавьте специалистов и услуги');
                sheet.close();
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать'),
            })
          }
        >
          {create.isPending ? 'Создаём…' : 'Создать'}
        </button>
      </div>
    </>
  );
}
